import { z } from 'zod';
/**
 * Money, everywhere in KinshipOS, is an integer number of paise.
 *
 * Never a float, never a decimal string, never rupees. `0.1 + 0.2 !== 0.3` in
 * IEEE 754, and a maintenance ledger that drifts by a paisa a month is a ledger
 * a treasurer stops trusting — which is the end of the product's usefulness to
 * the person who signs the cheque.
 *
 * The database stores `BigInt`; JSON carries a `number`, which is safe up to
 * 2^53 paise (about ₹90,000 crore) — comfortably past any society's corpus.
 */
export const PaiseSchema = z
    .number()
    .int('Amounts are stored in paise and must be whole numbers')
    .safe();
export const NonNegativePaiseSchema = PaiseSchema.nonnegative();
/** Converts rupees a human typed into the paise the system stores. */
export const rupeesToPaise = (rupees) => Math.round(rupees * 100);
/** Only for display. Never feed the result back into a calculation. */
export const paiseToRupees = (paise) => paise / 100;
/**
 * `₹1,72,800` — Indian digit grouping (2,2,3), not thousands.
 *
 * A committee treasurer reading `₹172,800` has to stop and re-parse it. The
 * grouping is not decoration; it is the difference between a number that is
 * read and one that is checked.
 */
export function formatPaise(paise, options = {}) {
    const { showPaise = false, symbol = true } = options;
    const negative = paise < 0;
    const magnitude = Math.abs(paise);
    const hasPaise = magnitude % 100 !== 0;
    const formatted = new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: showPaise || hasPaise ? 2 : 0,
        maximumFractionDigits: showPaise || hasPaise ? 2 : 0,
    }).format(showPaise || hasPaise ? magnitude / 100 : Math.trunc(magnitude / 100));
    // U+2212 minus, not a hyphen: it aligns with the digits in a tabular column.
    return `${negative ? '−' : ''}${symbol ? '₹' : ''}${formatted}`;
}
/** `₹1.7L`, `₹3.4Cr` — lakh and crore, never million. */
export function formatPaiseCompact(paise) {
    const rupees = Math.trunc(Math.abs(paise) / 100);
    const sign = paise < 0 ? '−' : '';
    const trim = (value) => {
        const rounded = Math.round(value * 10) / 10;
        return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
    };
    if (rupees >= 10_000_000)
        return `${sign}₹${trim(rupees / 10_000_000)}Cr`;
    if (rupees >= 100_000)
        return `${sign}₹${trim(rupees / 100_000)}L`;
    if (rupees >= 1_000)
        return `${sign}₹${trim(rupees / 1_000)}K`;
    return `${sign}₹${rupees}`;
}
/** The only currency the platform handles today. */
export const CurrencySchema = z.literal('INR');
//# sourceMappingURL=money.js.map